import React, { useEffect, useState, useCallback } from 'react';
import { Link2, Plus, Copy, Check, Trash2, BarChart3, Loader2 } from 'lucide-react';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Panel, PanelLoader, EmptyState, Card, Field, inputCls, copyText } from './shared';
import { toast } from 'sonner';

const SOURCES = ['instagram', 'facebook', 'tiktok', 'youtube', 'linkedin', 'email', 'sms', 'google', 'qr', 'print', 'influencer'];
const MEDIUMS = ['social', 'cpc', 'email', 'sms', 'referral', 'organic', 'print', 'display'];

export default function LinksPanel({ campaigns = [] }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({
    title: '', destination: '', slug: '', campaign_id: '', channel: '',
    utm: { source: '', medium: '', campaign: '', content: '', term: '' },
  });

  const load = useCallback(() => {
    suiteAPI.listLinks().then(r => setLinks(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.destination.trim()) return toast.error('Add a destination URL');
    setSaving(true);
    try {
      await suiteAPI.createLink({ ...form, base_url: window.location.origin });
      toast.success('Smart link created');
      setForm({ title: '', destination: '', slug: '', campaign_id: '', channel: '', utm: { source: '', medium: '', campaign: '', content: '', term: '' } });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create link'); }
    finally { setSaving(false); }
  };

  const copy = (text, id) => {
    copyText(text);
    setCopied(id);
    toast.success('Copied');
    setTimeout(() => setCopied(null), 1500);
  };

  const remove = async (id) => { await suiteAPI.deleteLink(id); setLinks(p => p.filter(l => l.id !== id)); };

  const utmField = (k, options) => (
    <Field label={`utm_${k}`}>
      <Input
        value={form.utm[k]}
        onChange={e => setForm(f => ({ ...f, utm: { ...f.utm, [k]: e.target.value } }))}
        list={options ? `utm-${k}` : undefined}
        className={inputCls}
        data-testid={`utm-${k}`}
      />
      {options && <datalist id={`utm-${k}`}>{options.map(o => <option key={o} value={o} />)}</datalist>}
    </Field>
  );

  return (
    <Panel title="Smart Links & UTM Builder" subtitle="Build tagged, shortened, trackable links for every channel. Clicks roll straight into campaign attribution." testId="suite-links"
      action={<a href={suiteAPI.exportCsvUrl('links')} target="_blank" rel="noreferrer"><Button className="bg-zinc-800 text-white">Export CSV</Button></a>}>
      <div className="grid lg:grid-cols-[400px_1fr] gap-6">
        <Card className="p-5 space-y-4 h-fit">
          <Field label="Link name"><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="IG bio — September drop" className={inputCls} data-testid="link-title" /></Field>
          <Field label="Destination URL"><Input value={form.destination} onChange={e => setForm({ ...form, destination: e.target.value })} placeholder="https://yoursite.com/drop" className={inputCls} data-testid="link-destination" /></Field>
          <Field label="Custom slug (optional)" hint="Leave blank for a random short code."><Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="sept-drop" className={inputCls} data-testid="link-slug" /></Field>
          <Field label="Campaign">
            <select value={form.campaign_id} onChange={e => setForm({ ...form, campaign_id: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3 py-2" data-testid="link-campaign">
              <option value="">No campaign</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            {utmField('source', SOURCES)}
            {utmField('medium', MEDIUMS)}
            {utmField('campaign')}
            {utmField('content')}
          </div>
          <Button onClick={create} disabled={saving} className="w-full bg-[#b8ff00] text-black font-bold" data-testid="link-create-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />} Create smart link
          </Button>
        </Card>

        <div className="space-y-3">
          {loading ? <PanelLoader /> : links.length === 0 ? (
            <EmptyState icon={Link2} text="No links yet. Build your first tracked link on the left." />
          ) : links.map(l => (
            <Card key={l.id} className="p-4" data-testid={`link-item-${l.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white truncate">{l.title}</p>
                  <p className="text-[11px] text-[#b8ff00] font-mono truncate">{l.short_url}</p>
                  <p className="text-[11px] text-zinc-500 truncate mt-0.5">{l.full_url}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300">{l.clicks} clicks</span>
                  <Button size="sm" variant="ghost" className="text-zinc-400" onClick={() => copy(l.short_url, l.id)} data-testid={`link-copy-${l.id}`}>{copied === l.id ? <Check size={13} /> : <Copy size={13} />}</Button>
                  <Button size="sm" variant="ghost" className="text-zinc-400" onClick={() => suiteAPI.linkAnalytics(l.id).then(r => setStats(r.data))} data-testid={`link-stats-${l.id}`}><BarChart3 size={13} /></Button>
                  <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => remove(l.id)}><Trash2 size={13} /></Button>
                </div>
              </div>
            </Card>
          ))}

          {stats && (
            <Card className="p-5" data-testid="link-analytics">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-white">{stats.link.title} — {stats.total_clicks} clicks</h3>
                <Button size="sm" variant="ghost" className="text-zinc-500" onClick={() => setStats(null)}>Close</Button>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Clicks by day</p>
                  {stats.by_day.length === 0 ? <p className="text-xs text-zinc-500">No clicks yet.</p> : (
                    <div className="flex items-end gap-1.5 h-24">
                      {stats.by_day.map(d => (
                        <div key={d.date} className="flex-1 bg-[#b8ff00]/70 rounded-t" style={{ height: `${Math.max(8, (d.clicks / Math.max(...stats.by_day.map(x => x.clicks))) * 100)}%` }} title={`${d.date}: ${d.clicks}`} />
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Referrers</p>
                  {stats.by_referrer.slice(0, 6).map(r => (
                    <div key={r.referrer} className="flex justify-between py-1 border-b border-zinc-800 last:border-0">
                      <span className="text-xs text-zinc-400 truncate max-w-[70%]">{r.referrer}</span><span className="text-xs text-white">{r.clicks}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </Panel>
  );
}
